import { InvoiceLineItem, LineItemInput } from "api/billing";
import { paiseToRupeesInput, rupeesToPaise } from "utils/format";
import { LineItemDraft } from "./types";

// Line-item drafts for the charge / invoice editors, mirroring the API rules
// (validations/billingValidation.js): 1–50 lines, qty 1–10000, |amount| ≤ ₹100 crore, total ≥ ₹1.

export const MIN_TOTAL_PAISE = 100;
const MAX_LINE_PAISE = 100000000000;
const MAX_LINES = 50;

let keySeed = 0;
const nextKey = () => {
  keySeed += 1;
  return `line-${Date.now().toString(36)}-${keySeed}`;
};

export const newLineItem = (kind: LineItemDraft["kind"] = "charge"): LineItemDraft => ({
  key: nextKey(),
  kind,
  description: kind === "discount" ? "Discount" : "",
  qty: "1",
  amount: "",
});

export const draftsFromItems = (items: InvoiceLineItem[]): LineItemDraft[] =>
  items.length === 0
    ? [newLineItem()]
    : items.map((item) => ({
        key: nextKey(),
        kind: item.unitPrice < 0 ? "discount" : "charge",
        description: item.description,
        qty: String(item.qty),
        amount: paiseToRupeesInput(Math.abs(item.unitPrice)),
      }));

const parseQty = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const qty = Number(trimmed);
  return qty >= 1 && qty <= 10000 ? qty : null;
};

/** Signed unit price in paise (discounts negative), or null while invalid. */
export const unitPricePaise = (draft: LineItemDraft): number | null => {
  const paise = rupeesToPaise(draft.amount);
  if (paise === null || paise <= 0 || paise > MAX_LINE_PAISE) {
    return null;
  }
  return draft.kind === "discount" ? -paise : paise;
};

export const lineTotalPaise = (draft: LineItemDraft): number | null => {
  const qty = parseQty(draft.qty);
  const price = unitPricePaise(draft);
  return qty === null || price === null ? null : qty * price;
};

export interface LineItemsValidation {
  /** API payload when every line is valid. */
  items: LineItemInput[] | null;
  /** Error per draft key. */
  errors: Record<string, string>;
  /** Sum of the valid lines (paise). */
  total: number;
  totalError: string | null;
}

export const validateLineItems = (drafts: LineItemDraft[]): LineItemsValidation => {
  const errors: Record<string, string> = {};
  const items: LineItemInput[] = [];
  let total = 0;
  drafts.forEach((draft) => {
    const description = draft.description.trim();
    const qty = parseQty(draft.qty);
    const price = unitPricePaise(draft);
    if (!description) {
      errors[draft.key] = "Describe this line.";
    } else if (description.length > 200) {
      errors[draft.key] = "Keep the description under 200 characters.";
    } else if (qty === null) {
      errors[draft.key] = "Quantity must be a whole number from 1 to 10000.";
    } else if (price === null) {
      errors[draft.key] = "Enter an amount greater than ₹0.";
    }
    if (qty !== null && price !== null) {
      total += qty * price;
      if (!errors[draft.key]) {
        items.push({ description, qty, unitPrice: price });
      }
    }
  });
  let totalError: string | null = null;
  if (drafts.length === 0) {
    totalError = "Add at least one line.";
  } else if (drafts.length > MAX_LINES) {
    totalError = `Use at most ${MAX_LINES} lines.`;
  } else if (Object.keys(errors).length === 0 && total < MIN_TOTAL_PAISE) {
    totalError = "The total must be at least ₹1 (discounts can't exceed the charges).";
  }
  const valid = Object.keys(errors).length === 0 && !totalError;
  return { items: valid ? items : null, errors, total, totalError };
};

export const MAX_LINE_ITEMS = MAX_LINES;
